import type { z } from 'zod';
import type * as E from '../entities/index.js';
import { ETAPES_CLIENT } from '../entities/index.js';
import { BELENUS, IDS, INSTANT, uuid } from './belenus.js';

/**
 * Un exemple par route du contrat (« Famille Bélénus »). Ils alimentent les handlers MSW (écrans rendus
 * uniquement avec les mocks), le serveur de mock du MCP (L40) et les tests de composants (L50+).
 * Côté portail, l'exemple est celui de Marc B. (héritier) : la mandante et Hélène n'y sont jamais nommées.
 */

export interface Exemple {
  parametres?: Record<string, string>;
  requete?: Record<string, string>;
  corps?: unknown;
  reponse: { status: number; corps?: unknown };
}

type T<S extends z.ZodType> = z.infer<S>;

const ok = (corps: unknown, autres: Omit<Exemple, 'reponse'> = {}, status = 200): Exemple => ({ ...autres, reponse: { status, corps } });
const cree = (corps: unknown, autres: Omit<Exemple, 'reponse'> = {}): Exemple => ok(corps, autres, 201);
const D = { dossierId: IDS.dossier };
const euros = (e: number) => ({ amountCents: Math.round(e * 100), currency: 'EUR' as const });
const page = <I>(items: I[]) => ({ items, nextCursor: null });

const marc = BELENUS.personnes.find((p) => p.id === IDS.marc) as T<typeof E.PersonStaffViewSchema>;

const utilisateur = (id: string, nom: string, email: string, role: T<typeof E.RoleSchema>): T<typeof E.UserSchema> => ({
  id,
  email,
  displayName: nom,
  kind: role === 'clerc' ? 'external' : role === 'heritier' ? 'portal' : 'staff',
  status: 'actif',
  roles: [{ role, scopeType: 'global', scopeId: null, validUntil: null }],
});

const isabelle = utilisateur(IDS.isabelle, 'Isabelle F.', 'isabelle@cabinet-maat.test', 'dirigeante');
const brigitte = utilisateur(IDS.brigitte, 'Brigitte', 'brigitte@cabinet-maat.test', 'assistante');
const cynthia = utilisateur(IDS.cynthia, 'Cynthia', 'cynthia@etude-principale.test', 'clerc');

const session: T<typeof E.SessionSchema> = {
  user: brigitte,
  effectiveUser: null,
  impersonation: null,
  permissions: ['dossier:read:dossier', 'followup:write:dossier', 'message:write:dossier'],
  expiresAt: '2026-10-14T18:00:00-04:00',
};

const sessionImpersonation: T<typeof E.ImpersonationSessionSchema> = {
  id: IDS.impersonation,
  actorUserId: IDS.isabelle,
  targetUserId: IDS.brigitte,
  reason: 'Vérifier la file du jour de Brigitte',
  mode: 'read',
  startedAt: INSTANT,
  endedAt: null,
  endedReason: null,
};

const etude: T<typeof E.EtudeSchema> = {
  id: IDS.etude,
  name: 'Étude principale',
  address: 'Pointe-à-Pitre',
  notaireTitulaire: 'Notaire titulaire (fictif)',
  status: 'active',
  members: [{ userId: IDS.cynthia, displayName: 'Cynthia', function: 'clerc' }],
  rowVersion: 2,
};

const tache: T<typeof E.TaskSchema> = {
  id: IDS.tache,
  dossierId: IDS.dossier,
  personId: IDS.helene,
  kind: 'appel',
  title: 'Appeler Hélène T. : deux messages lus sans réponse',
  assigneeUserId: IDS.brigitte,
  dueAt: INSTANT,
  status: 'a_faire',
  rowVersion: 1,
};

const decision: T<typeof E.DecisionSchema> = {
  id: IDS.decision,
  dossierId: IDS.dossier,
  question: 'Branche Japon injoignable depuis 3 semaines : faut-il mandater un généalogiste ?',
  options: [
    { code: 'genealogiste', label: 'Mandater un généalogiste', costEstimate: euros(1200) },
    { code: 'attendre', label: 'Attendre un mois de plus', costEstimate: null },
  ],
  reservedToRole: 'dirigeante',
  decidedOption: null,
  decidedAt: null,
};

const journal: T<typeof E.TimelineEntrySchema> = {
  id: IDS.journal,
  dossierId: IDS.dossier,
  personId: IDS.marc,
  kind: 'piece',
  at: '2026-10-11T10:12:00-04:00',
  summary: 'Marc B. a envoyé une photo de son acte de naissance (illisible, meilleure photo demandée).',
  visibility: 'externe',
};

const exportPdf: T<typeof E.ExportSchema> = {
  documentId: IDS.export,
  downloadUrl: '/api/v1/exports/journal-BEL-2026-001.pdf',
  format: 'pdf',
  expiresAt: '2026-10-14T10:00:00-04:00',
};

const relationSansId: T<typeof E.RelationshipCreateSchema> = {
  kind: 'filiation',
  personAId: IDS.marie,
  personBId: IDS.marc,
  unionType: null,
  filiationType: 'biologique',
  startDate: null,
  endDate: null,
};

const relation: T<typeof E.RelationshipSchema> = { id: IDS.relation, ...relationSansId };

const arbre: T<typeof E.FamilyTreeSchema> = {
  dossierId: IDS.dossier,
  nodes: BELENUS.personnes.map((p) => ({
    id: p.id,
    displayName: p.firstNames ? `${p.firstNames} ${p.lastName}` : 'Fille de Lucien (prénom inconnu)',
    vitalStatus: p.vitalStatus,
    isHypothesis: p.isHypothesis,
    generation: [IDS.albert, IDS.rose].includes(p.id as never)
      ? 0
      : [IDS.marc, IDS.sandra, IDS.filleLucien].includes(p.id as never)
        ? 2
        : 1,
    role: p.role,
    reachability: p.vitalStatus === 'vivant' || p.vitalStatus === 'inconnu' ? p.reachability : null,
    maskedForFamily: p.contacts.some((c) => c.maskedForFamily),
    contacts: p.contacts.map((c) => ({ type: c.type, value: c.value, visibility: c.visibility })),
  })),
  relationships: BELENUS.relations.map(([a, b, kind], i) => ({
    id: uuid(0xb00 + i),
    kind,
    personAId: a,
    personBId: b,
    unionType: kind === 'union' ? ('mariage' as const) : null,
    filiationType: kind === 'filiation' ? ('biologique' as const) : null,
    startDate: null,
    endDate: null,
  })),
  successions: [...BELENUS.successions],
  devolutions: [BELENUS.devolution],
  completeness: 82,
};

const civil = (p: T<typeof E.PersonStaffViewSchema>): T<typeof E.PersonClercViewSchema> => ({
  id: p.id,
  firstNames: p.firstNames,
  lastName: p.lastName,
  birthName: p.birthName,
  birthDate: p.birthDate,
  birthPlace: p.birthPlace,
  deathDate: p.deathDate,
  deathPlace: p.deathPlace,
  vitalStatus: p.vitalStatus,
  isHypothesis: p.isHypothesis,
  relationLabel: p.relationLabel,
  identityVerified: !p.isHypothesis && p.vitalStatus !== 'inconnu',
  contactsOnRequest: true,
});

const interrogation: T<typeof E.NotaryInquirySchema> = {
  id: IDS.interrogation,
  dossierId: IDS.dossier,
  successionId: IDS.succAlbert,
  kind: 'FCDDV',
  status: 'revenue',
  sentAt: '2026-09-20',
  returnedAt: '2026-10-02',
  documentId: null,
  rowVersion: 3,
};

const renonciation: T<typeof E.RenunciationSchema> = {
  id: IDS.renonciation,
  successionId: IDS.succLucien,
  personId: IDS.filleLucien,
  filedAt: null,
  court: 'TJ Pointe-à-Pitre',
  attestationReceivedAt: null,
};

const document: T<typeof E.DocumentSchema> = {
  id: IDS.documentActe,
  dossierId: IDS.dossier,
  title: 'Acte de décès d’Albert B. (spécimen)',
  category: 'etat_civil',
  status: 'valide',
  visibility: 'notaire',
  currentVersion: {
    versionNo: 1,
    mime: 'application/pdf',
    size: 48213,
    sha256: 'a3f1c2d4e5b60718293a4b5c6d7e8f90112233445566778899aabbccddeeff00',
    source: 'portail',
    preview: { thumbnailUrl: '/api/v1/apercus/acte-deces-albert.webp', pages: 1, durationS: null },
    quality: { blurScore: 0.12, readable: true },
  },
  links: [{ targetType: 'person', targetId: IDS.albert }],
  receivedAt: '2026-09-22T11:00:00-04:00',
};

const exigence: T<typeof E.DocumentRequirementSchema> = {
  id: IDS.exigence,
  personId: IDS.marc,
  assetId: null,
  kind: 'acte_naissance',
  label: 'Acte de naissance de Marc B. (mairie des Abymes)',
  status: 'demande',
  requestedAt: '2026-10-11T10:30:00-04:00',
  delegatedRequest: false,
};

const mandat: T<typeof E.MandateSchema> = {
  id: IDS.mandat,
  dossierId: IDS.dossier,
  kind: 'mandat_recherche',
  signerPersonId: IDS.josiane,
  provider: 'docuseal',
  status: 'signe',
  signedAt: '2026-09-18T10:00:00-04:00',
  documentId: null,
};

const compteBrigitte: T<typeof E.ChannelAccountSchema> = {
  id: IDS.waBrigitte,
  label: 'WhatsApp de Brigitte',
  kind: 'whatsapp',
  requiresValidation: false,
};

const fil: T<typeof E.ThreadSchema> = {
  id: IDS.fil,
  dossierId: IDS.dossier,
  personId: IDS.marc,
  channelAccount: compteBrigitte,
  externalContact: '+590690000107',
  status: 'rattache',
  lastMessageAt: '2026-10-11T10:12:00-04:00',
  lastMessagePreview: 'Vocal (0:42) : « Ma mère avait un frère au Moule… »',
  unreadCount: 1,
};

const message: T<typeof E.MessageSchema> = {
  id: IDS.message,
  threadId: IDS.fil,
  direction: 'entrant',
  body: '',
  sentAt: '2026-10-11T10:12:00-04:00',
  status: 'recu',
  senderDisplayName: 'Marc B.',
  attachments: [{ documentVersionId: uuid(0x9a1), mime: 'audio/ogg', thumbnailUrl: null, pages: null, durationS: 42 }],
  transcript: { text: 'Ma mère avait un frère au Moule, je crois.', status: 'a_verifier' },
};

const sortant: T<typeof E.OutboundMessageSchema> = {
  id: IDS.sortant,
  channelAccountId: IDS.waBrigitte,
  personId: IDS.marc,
  dossierId: IDS.dossier,
  templateCode: 'R2',
  renderedBody: 'Bonjour Marc, il nous manque encore votre acte de naissance. Une difficulté ? Répondez-moi. Cabinet MAAT',
  scheduledAt: '2026-10-15T09:00:00-04:00',
  state: 'scheduled',
  validationRequiredByRole: null,
  attempts: 0,
};

const plan: T<typeof E.FollowupPlanSchema> = {
  id: IDS.plan,
  dossierId: IDS.dossier,
  personId: IDS.marc,
  organisation: null,
  cadenceName: 'Cadence héritier (par défaut)',
  state: 'actif',
  pausedReason: null,
  pausedUntil: null,
  nextActionAt: '2026-10-15T09:00:00-04:00',
  nextAction: { kind: 'message', channel: 'whatsapp', templateCode: 'R2' },
};

const modele: T<typeof E.TemplateSchema> = {
  code: 'A1',
  channel: 'whatsapp',
  recipient: 'famille',
  versionNo: 3,
  status: 'valide_isabelle',
  body: 'Bonjour {prenom}, le cabinet accompagne la famille dans la succession de {defunt}. Voici votre espace : {lien_portail}. {signature}',
  variables: ['prenom', 'defunt', 'lien_portail', 'signature'],
  lints: [],
};

const ligne = (
  personId: string,
  nom: string,
  du: number,
  paye: number,
  statut: T<typeof E.HeirBillingLineSchema>['status'],
  bloque: boolean,
) => ({
  personId,
  displayName: nom,
  due: euros(du),
  paid: euros(paye),
  remaining: euros(du - paye),
  status: statut,
  blocksSignature: bloque,
});

const regles: T<typeof E.BillingRuleSetSchema> = {
  versionNo: 2,
  reason: 'Échéancier accordé à Marc B.',
  createdAt: '2026-10-05T15:00:00-04:00',
  items: [
    { ruleCode: 'repartition_quote_part', enabled: true, params: { base: 'parts_calculees' } },
    { ruleCode: 'echeancier', enabled: true, params: { personne: 'marc', echeances: 2 } },
  ],
};

const facturation: T<typeof E.BillingStaffViewSchema> = {
  dossierId: IDS.dossier,
  ruleSet: regles,
  perHeir: [
    ligne(IDS.josiane, 'Josiane B.', 1500, 1500, 'a_jour', false),
    ligne(IDS.marc, 'Marc B.', 750, 0, 'echeancier', false),
    ligne(IDS.helene, 'Hélène T.', 1500, 0, 'impaye', true),
    ligne(IDS.sandra, 'Sandra B.', 750, 0, 'impaye', true),
  ],
  invoices: [
    {
      id: IDS.facture,
      number: 'MAAT-F-2026-0042',
      issuedAt: '2026-09-18T12:00:00-04:00',
      payerPersonId: IDS.josiane,
      total: euros(1500),
      status: 'payee',
    },
  ],
  payments: [
    {
      id: IDS.paiement,
      personId: IDS.josiane,
      amount: euros(1500),
      status: 'reussi',
      paidAt: '2026-09-20T09:30:00-04:00',
      provider: 'stripe',
    },
  ],
  collected: euros(1500),
  outstanding: euros(3000),
  notaryFeesCollectedByCabinet: false,
};

const collecte: T<typeof E.CollectionScheduleSchema> = {
  dossierId: IDS.dossier,
  signatureDate: '2026-11-30',
  marginDays: 7,
  deadline: '2026-11-23',
  installments: [
    { id: IDS.echeanceMarc1, personId: IDS.marc, amount: euros(375), dueAt: '2026-10-25', isManualOverride: false, status: 'a_venir' },
    { id: IDS.echeanceMarc2, personId: IDS.marc, amount: euros(375), dueAt: '2026-11-20', isManualOverride: false, status: 'a_venir' },
  ],
};

const consentement: T<typeof E.ConsentSchema> = {
  id: IDS.consentement,
  personId: IDS.marc,
  purpose: 'contact_cabinet',
  granted: true,
  channel: 'portail',
  capturedAt: '2026-10-02T18:40:00-04:00',
};

const sensible: T<typeof E.SensitiveLabelSchema> = {
  id: IDS.sensible,
  personId: IDS.helene,
  label: 'Deuil récent, ne contacter que par le cabinet',
  note: 'Préférer un appel de Brigitte en fin de matinée.',
  shares: [],
};

const note: T<typeof E.InternalNoteSchema> = {
  id: IDS.note,
  dossierId: IDS.dossier,
  personId: IDS.filleLucien,
  body: 'Piste : un profil au Japon vu sur Facebook, à vérifier.',
  isHypothesis: true,
  authorDisplayName: 'Brigitte',
  createdAt: '2026-10-09T16:20:00-04:00',
};

const compteRendu: T<typeof E.CompteRenduSchema> = {
  id: IDS.compteRendu,
  dossierId: IDS.dossier,
  personId: IDS.marc,
  kind: 'appel',
  title: 'Appel avec Marc B.',
  status: 'a_relire',
  sourceText: 'Marc confirme l’adresse de sa sœur Sandra au Moule.',
  body: [
    'Compte rendu d’appel',
    'Dossier : Succession Bélénus',
    'Personne concernée : Marc Bélénus',
    'Date : 14/10/2026',
    '',
    'Ce qui a été dit',
    'Marc confirme l’adresse de sa sœur Sandra au Moule.',
    '',
    'À vérifier : compte rendu généré automatiquement, à relire avant tout usage.',
    'Cabinet MAAT',
  ].join('\n'),
  createdAt: '2026-10-14T09:00:00-04:00',
  createdByDisplayName: 'Brigitte',
  generatedAt: '2026-10-14T09:05:00-04:00',
  validatedAt: null,
  validatedByDisplayName: null,
  rowVersion: 2,
};
// RG-24 : une période de dix minutes chronométrée par Brigitte, puis une correction motivée.
const etapeDemarrage = {
  codeEtape: 'consultation_diagnostic',
  versionCatalogue: '2026-10-30.1',
  etat: 'fait' as const,
  faitLe: '2026-10-30T09:00:00-04:00',
  faitPar: 'Isabelle',
  reponse: null,
  motif: null,
};
const vocalExemple: T<typeof E.VocalSchema> = {
  id: '0192a6c0-0000-7000-8000-0000000000b1',
  nomFichier: 'entretien.m4a',
  mime: 'audio/mp4',
  taille: 4,
  recus: 4,
  statut: 'transcription_en_attente',
  transcription: null,
  deposePar: 'Brigitte',
  deposeLe: '2026-10-30T09:00:00-04:00',
};
const demarrageDossier: T<typeof E.DemarrageAvancementSchema> = { dossierId: IDS.dossier, etapes: [etapeDemarrage] };

const tempsDossier: T<typeof E.TempsDossierSchema> = {
  dossierId: IDS.dossier,
  totalMs: 600_000,
  entries: [
    {
      id: '0192a6c0-0000-7000-8000-000000000f01',
      kind: 'chrono',
      userId: IDS.brigitte,
      durationMs: 600_000,
      startedAt: '2026-10-27T09:00:00-04:00',
      endedAt: '2026-10-27T09:10:00-04:00',
      reason: null,
      createdAt: '2026-10-27T09:10:00-04:00',
    },
  ],
};
const correctionTemps: T<typeof E.TempsEntreeSchema> = {
  id: '0192a6c0-0000-7000-8000-000000000f02',
  kind: 'correction',
  userId: IDS.brigitte,
  durationMs: 2_100_000,
  startedAt: null,
  endedAt: null,
  reason: 'Appel de 35 min à Marc non chronométré',
  createdAt: '2026-10-27T10:00:00-04:00',
};

const compteRenduAFaire: T<typeof E.CompteRenduSchema> = {
  ...compteRendu,
  id: uuid(0xa16),
  personId: null,
  kind: 'entretien',
  title: 'Entretien avec Nadia C.',
  status: 'a_faire',
  sourceText: null,
  body: null,
  generatedAt: null,
  rowVersion: 1,
};
const acteNotarie: T<typeof E.ActeNotarieSchema> = {
  id: IDS.acteNotarie,
  dossierId: IDS.dossier,
  successionId: IDS.succAlbert,
  deceasedDisplayName: 'Albert Bélénus',
  kind: 'notoriete',
  kindLabel: 'Acte de notoriété',
  status: 'signe',
  actDate: '2012-05-03',
  etude: 'Étude fictive du Moule',
  reference: 'NOT-2012-118',
  documentId: IDS.documentActe,
  documentTitle: 'Acte de notoriété Albert Bélénus',
  note: null,
  createdAt: '2026-10-14T09:00:00-04:00',
  createdByDisplayName: 'Brigitte',
  rowVersion: 1,
};
const videoDossier: T<typeof E.VideoDossierSchema> = {
  id: IDS.video,
  dossierId: IDS.dossier,
  title: 'Présentation du cabinet',
  url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
  youtubeId: 'dQw4w9WgXcQ',
  embedUrl: 'https://www.youtube-nocookie.com/embed/dQw4w9WgXcQ',
  createdAt: '2026-10-14T09:00:00-04:00',
  createdByDisplayName: 'Brigitte',
  sharedWithFamilyAt: null,
  sharedByDisplayName: null,
  sharedWith: [],
  rowVersion: 1,
};
const destinataireVideo = { userId: IDS.compteMarc, displayName: 'Marc Bélénus' };
const designationRegsuc: T<typeof E.DesignationSchema> = {
  code: 'REGSUC-SUPERDEFTRANS',
  intitule: 'REGULARISATION SUCCESSION PERSONNELLE DU DEFUNT ET TRANSFERT DU DEFUNT AUX VIVANTS',
  detail: [
    { titre: 'Identification du patrimoine du défunt désigné', lignes: ['Définition du besoin', 'État des lieux et diagnostic du dossier'] },
    { titre: 'Intermédiation entre les différents protagonistes', lignes: [] },
  ],
  catalogueVersion: '1.0.0',
};
const devisDetaille: T<typeof E.QuoteSchema> = {
  id: IDS.devisDetaille,
  dossierId: IDS.dossier,
  phaseScope: 'jusqu_indivision',
  status: 'brouillon',
  total: { amountCents: 250_000, currency: 'EUR' },
  createdAt: '2026-10-30T09:00:00-04:00',
  lines: [{ id: IDS.ligneDevis, position: 1, designation: designationRegsuc, amount: { amountCents: 250_000, currency: 'EUR' } }],
};
const supplement: T<typeof E.BillingSupplementSchema> = {
  id: IDS.supplement,
  dossierId: IDS.dossier,
  code: 'recherche_titre',
  label: 'Recherche de titre',
  unitAmountCents: 15_000,
  quantity: 1,
  totalCents: 15_000,
  billable: true,
  note: null,
  createdAt: '2026-10-14T09:00:00-04:00',
  createdByDisplayName: 'Brigitte',
  invoiced: false,
  rowVersion: 1,
};
const CSV_IMPORT = 'prenom;nom;telephone\nNadia;Fictive;0690000001\nnadia;FICTIVE;\n;Sans;';
const ligneImport = (
  line: number,
  firstNames: string,
  lastName: string,
  outcome: 'a_importer' | 'doublon' | 'erreur',
  reason: string | null,
) => ({
  line,
  firstNames,
  lastName,
  vitalStatus: 'inconnu' as const,
  role: 'heritier' as const,
  phone: line === 2 ? '0690000001' : null,
  email: null,
  preferredChannel: null,
  relationLabel: null,
  outcome,
  reason,
});
const apercuImport: T<typeof E.ImportContactsPreviewSchema> = {
  fingerprint: '5f2b51ad3a5bf4c3c3a0dd8f3a1b42c3a7b0e1e1f5c0e6a0c3c8b6a4d2e1f0a9',
  fileName: 'contacts.csv',
  separator: ';',
  totalRows: 3,
  importableCount: 1,
  duplicateCount: 1,
  errorCount: 1,
  preview: [
    ligneImport(2, 'Nadia', 'Fictive', 'a_importer', null),
    ligneImport(3, 'nadia', 'FICTIVE', 'doublon', 'Même nom que la ligne 2 du fichier.'),
    ligneImport(4, '', 'Sans', 'erreur', 'Prénom manquant.'),
  ],
  duplicates: [{ line: 3, reason: 'Même nom que la ligne 2 du fichier.' }],
  errors: [{ line: 4, reason: 'Prénom manquant.' }],
  columnsRecognized: ['prenom', 'nom', 'telephone'],
  columnsIgnored: [],
  mandateSigned: true,
  mandateForcing: null,
  limits: { maxRows: 2000, maxBytes: 400_000, previewRows: 10 },
};
const dansDossier = { dossierReference: 'MAAT-2026-014', dossierTitle: 'Succession Bélénus' };

const parametre = {
  id: IDS.parametre,
  key: 'prescription.guadeloupe.annees',
  value: 10,
  effectiveFrom: '2024-04-10',
  effectiveTo: '2038-12-31',
  sourceUrl: 'https://www.legifrance.gouv.fr/jorf/id/JORFTEXT000049386121',
  changedBy: IDS.isabelle,
  note: 'Loi 2024-322, art. 51-III.',
};

const questionBeta: T<typeof E.BetaQuestionViewSchema> = {
  id: 'BETA-08',
  key: 'estimation.visible_client',
  question: 'Peut-on montrer aux clients l’estimation ?',
  options: [
    { code: 'jamais', label: 'Jamais' },
    { code: 'apres_titre', label: 'Après le titre trouvé' },
    { code: 'au_cas_par_cas', label: 'Au cas par cas' },
  ],
  default: 'jamais',
};

const reglage: T<typeof E.LearnedSettingSchema> = {
  key: 'brigitte_whatsapp_isabelle',
  value: 'avec_validation',
  updatedAt: '2026-10-10T08:00:00-04:00',
  updatedByDisplayName: 'Isabelle F.',
};

/* ----- Portail : vue de Marc B. (héritier). Ni la mandante, ni Hélène, ni aucun montant réglé. ----- */

const brancheMarc: T<typeof E.PersonFamilyViewSchema>[] = [
  {
    id: IDS.marc,
    displayName: 'Marc B.',
    relationLabel: null,
    vitalStatus: 'vivant',
    isHypothesis: false,
    sharedContacts: [],
    isViewer: true,
    contactStatus: null,
  },
  {
    id: IDS.marie,
    displayName: 'Marie B.',
    relationLabel: 'votre mère',
    vitalStatus: 'decede',
    isHypothesis: false,
    sharedContacts: [],
    isViewer: false,
    contactStatus: null,
  },
  {
    id: IDS.sandra,
    displayName: 'Sandra B.',
    relationLabel: 'votre sœur',
    vitalStatus: 'vivant',
    isHypothesis: false,
    sharedContacts: [],
    isViewer: false,
    contactStatus: null,
  },
];

const etatEtape = (i: number) => (i < 3 ? ('fait' as const) : i === 3 ? ('en_cours' as const) : ('a_venir' as const));

const facturationClient: T<typeof E.BillingClientViewSchema> = {
  upToDate: false,
  remainingDue: euros(750),
  nextInstallment: { dueAt: '2026-10-25', amount: euros(375) },
  receiptsUrl: '/api/v1/portail/recus',
};

const accueil: T<typeof E.PortalHomeSchema> = {
  dossierTitle: 'Succession Bélénus',
  greeting: 'Bonjour Marc, votre dossier avance.',
  steps: ETAPES_CLIENT.map((e, i) => ({
    code: e.code,
    label: e.libelle,
    state: etatEtape(i),
    detail: e.code === 'etat_civil' ? 'il manque la vôtre' : null,
  })),
  nextAction: {
    kind: 'piece',
    label: 'Une photo de votre acte de naissance (mairie des Abymes).',
    ctaLabel: 'Envoyer une photo',
    alternativeLabel: 'Je ne l’ai pas : demandez-le pour moi',
    requirementId: IDS.exigence,
  },
  branch: brancheMarc,
  billing: facturationClient,
  signature: 'Cabinet MAAT',
};

const confidentialite: T<typeof E.PortalPrivacySchema> = {
  consents: [
    { purpose: 'contact_cabinet', granted: true, capturedAt: '2026-10-02T18:40:00-04:00' },
    { purpose: 'partage_numero_famille', granted: false, capturedAt: '2026-10-02T18:40:00-04:00' },
  ],
  contactOnlyByCabinet: false,
  preferredChannel: 'email',
  dataRequestUrl: '/api/v1/demandes-rgpd',
};

const clercDossier: T<typeof E.ClercDossierSchema> = {
  id: IDS.dossier,
  reference: 'BEL-2026-001',
  title: 'Succession Bélénus',
  completeness: 82,
  readyForOpening: false,
  inquiries: [interrogation],
  missingPieces: [exigence],
};

const vivants = BELENUS.personnes.filter((p) => p.vitalStatus !== 'decede' && p.role !== 'auteur').map(civil);
const defunts = BELENUS.personnes.filter((p) => p.vitalStatus === 'decede').map(civil);

const recu = { received: true as const };
const webhook = (corps: Record<string, unknown>): Exemple => ok(recu, { corps }, 202);

export const EXEMPLES: Readonly<Record<string, Exemple>> = {
  'auth.connexion': ok(session, { corps: { email: 'brigitte@cabinet-maat.test', password: 'mot-de-passe-fictif', totp: '123456' } }),
  'auth.lienMagique': ok(
    {
      ...session,
      user: utilisateur(uuid(0x40a), 'Marc B.', 'marc.b@exemple.test', 'heritier'),
      permissions: ['portal:read:branche', 'portal:write:self'],
    },
    { corps: { token: '7KX2' } },
  ),
  'auth.moi': ok(session),
  'auth.otp.demander': cree({ challengeId: uuid(0x40b), expiresAt: '2026-10-14T09:10:00-04:00' }, { corps: { channel: 'whatsapp' } }),
  'auth.otp.verifier': ok(session, { corps: { code: '482913' } }),
  'auth.deconnexion': ok(undefined, {}, 204),
  'impersonation.demarrer': cree(sessionImpersonation, {
    corps: { targetUserId: IDS.brigitte, reason: 'Vérifier la file du jour de Brigitte', mode: 'read' },
  }),
  'impersonation.confirmer': cree(
    { confirmationId: uuid(0x40c), validUntil: '2026-10-14T09:05:00-04:00' },
    { parametres: { sessionId: IDS.impersonation }, corps: { action: 'taches.modifier', payloadHash: 'b'.repeat(64) } },
  ),
  'impersonation.terminer': cree(
    { ...sessionImpersonation, endedAt: '2026-10-14T09:20:00-04:00', endedReason: 'retour à ma vue' },
    { parametres: { sessionId: IDS.impersonation } },
  ),
  'impersonation.historique': ok(page([sessionImpersonation])),
  'dossiers.pilotage': ok({
    dossiers: [
      BELENUS.resume,
      {
        ...BELENUS.resume,
        id: IDS.dossierDemosthene,
        reference: 'DEM-2026-002',
        title: 'Succession Démosthène',
        blocker: null,
        currentPhase: 'P5',
        heirsCount: 6,
        respondedCount: 6,
        toFollowUpCount: 0,
        unreachableCount: 0,
      },
    ],
    pendingDecisions: 2,
    collected: euros(1500),
    outstanding: euros(3000),
    teamActivity: [
      { userDisplayName: 'Brigitte', actionsToday: 14 },
      { userDisplayName: 'Agent MAAT', actionsToday: 3 },
    ],
  } satisfies T<typeof E.DashboardSchema>),
  'dossiers.lister': ok(page([BELENUS.resume]), { requete: { phase: 'P3' } }),
  'dossiers.creer': cree(BELENUS.dossier, { corps: { title: 'Succession Bélénus', mandantPersonId: IDS.josiane, etudeId: IDS.etude } }),
  'dossiers.lire': ok(BELENUS.dossier, { parametres: D }),
  'dossiers.modifier': ok({ ...BELENUS.dossier, rowVersion: 8 }, { parametres: D, corps: { blocker: null } }),
  'dossiers.journal': ok(page([journal]), { parametres: D }),
  'dossiers.journal.exporter': ok(exportPdf, { parametres: D, requete: { destinataire: 'notaire' } }),
  'procedure.etapes': ok(BELENUS.etapes, { parametres: D }),
  'procedure.ajouter': cree(
    { id: uuid(0xa22), phase: 'P6', label: 'Sommation à opter', orderIndex: 7, status: 'a_faire', skippedReason: null, dueAt: null },
    { parametres: D, corps: { phase: 'P6', label: 'Sommation à opter', afterStepId: IDS.etape } },
  ),
  'procedure.reordonner': cree(BELENUS.etapes, { parametres: D, corps: { stepIds: BELENUS.etapes.map((e) => e.id) } }),
  'procedure.sauter': cree(BELENUS.etapes[1], {
    parametres: { dossierId: IDS.dossier, etapeId: uuid(0xa21) },
    corps: { reason: 'Décès d’Albert de plus de 6 ans (BETA-02)' },
  }),
  'taches.fileDuJour': ok(page([tache]), { requete: { jour: '2026-10-14' } }),
  'taches.creer': cree(tache, {
    corps: {
      dossierId: IDS.dossier,
      personId: IDS.helene,
      kind: 'appel',
      title: tache.title,
      assigneeUserId: IDS.brigitte,
      dueAt: INSTANT,
    },
  }),
  'taches.modifier': ok({ ...tache, status: 'fait', rowVersion: 2 }, { parametres: { tacheId: IDS.tache }, corps: { status: 'fait' } }),
  'decisions.lister': ok([decision]),
  'decisions.trancher': cree(
    { ...decision, decidedOption: 'attendre', decidedAt: INSTANT },
    { parametres: { decisionId: IDS.decision }, corps: { option: 'attendre' } },
  ),
  'genealogie.arbre': ok(arbre, { parametres: D }),
  'genealogie.personnes.creer': cree(marc, {
    parametres: D,
    corps: { firstNames: 'Marc', lastName: 'B.', vitalStatus: 'vivant', isHypothesis: false, role: 'heritier' },
  }),
  'genealogie.personnes.rechercher': ok(
    page([{ id: IDS.marc, dossierId: IDS.dossier, displayName: 'Marc B.', vitalStatus: 'vivant', relationLabel: 'petit-fils' }]),
    { requete: { q: 'Marc' } },
  ),
  'genealogie.personne.lire': ok(marc, { parametres: { personId: IDS.marc } }),
  'genealogie.personne.modifier': ok(
    { ...marc, birthPlace: 'Les Abymes', rowVersion: 2 },
    { parametres: { personId: IDS.marc }, corps: { birthPlace: 'Les Abymes' } },
  ),
  'genealogie.coordonnees.ajouter': cree(
    {
      id: uuid(0xc0a1),
      type: 'phone',
      value: '+590690000150',
      visibility: 'equipe',
      maskedForFamily: false,
      isPreferred: false,
      timezone: null,
    },
    { parametres: { personId: IDS.marc }, corps: { type: 'phone', value: '+590690000150' } },
  ),
  'genealogie.relations.creer': cree(relation, { parametres: D, corps: relationSansId }),
  'genealogie.fratrie.ajouter': cree(
    { person: marc, createdParents: [], relationships: [relation] },
    {
      parametres: D,
      corps: {
        person: { firstNames: 'Marc', lastName: 'B.', vitalStatus: 'vivant', isHypothesis: false, role: 'heritier' },
        siblingOfPersonId: IDS.sandra,
        siblingKind: 'germain',
        commonParentId: null,
      },
    },
  ),
  'genealogie.options.enregistrer': cree(
    { successionId: IDS.succAlbert, personId: IDS.josiane, option: 'acceptation', datedAt: '2026-09-25', protection: 'aucune' },
    {
      parametres: { successionId: IDS.succAlbert },
      corps: { personId: IDS.josiane, option: 'acceptation', datedAt: '2026-09-25', protection: 'aucune' },
    },
  ),
  'genealogie.devolution': ok([BELENUS.devolution], { parametres: D }),
  'genealogie.tableaux.exporter': ok(
    { ...exportPdf, documentId: uuid(0xa15) },
    { parametres: D, requete: { tableau: 'vivants', format: 'pdf' } },
  ),
  'biens.lister': ok([BELENUS.terrain], { parametres: D }),
  'biens.creer': cree(BELENUS.terrain, {
    parametres: D,
    corps: {
      kind: 'terrain',
      label: 'Terrain de Grand-Bourg',
      cadastralRef: 'AB 0123',
      commune: 'Grand-Bourg',
      areaM2: 6000,
      pluZoning: 'agricole',
      occupancy: 'vacant',
      titleStatus: 'trouve',
    },
  }),
  'estimations.lire': ok(BELENUS.estimation, { parametres: D, requete: { scenario: 'vendre' } }),
  'notaire.interrogations': ok([interrogation], { parametres: D }),
  'notaire.interrogations.modifier': ok(
    { ...interrogation, rowVersion: 4 },
    { parametres: { interrogationId: IDS.interrogation }, corps: { status: 'revenue', returnedAt: '2026-10-02' } },
  ),
  'notaire.renonciations': ok([renonciation], { parametres: D }),
  'echeances.lister': ok([BELENUS.echeance], { requete: { dossierId: IDS.dossier } }),
  'documents.lister': ok([document], { parametres: D }),
  'documents.televerser': cree(
    { documentId: uuid(0x903), uploadUrl: '/api/v1/televersements/0192a6c0-tus', expiresAt: '2026-10-14T21:00:00-04:00' },
    {
      parametres: D,
      corps: {
        title: 'Acte de naissance de Marc B.',
        category: 'etat_civil',
        mime: 'image/jpeg',
        size: 812345,
        links: [{ targetType: 'person', targetId: IDS.marc }],
      },
    },
  ),
  'documents.lire': ok(document, { parametres: { documentId: IDS.documentActe } }),
  'documents.examiner': cree(document, { parametres: { documentId: IDS.documentActe }, corps: { decision: 'valider' } }),
  'documents.attendus': ok([exigence], { parametres: D }),
  'mandats.lister': ok([mandat], { parametres: D }),
  'mandats.envoyer': cree(
    { ...mandat, id: IDS.devis, kind: 'devis', status: 'envoye', signedAt: null },
    { parametres: D, corps: { kind: 'devis', signerPersonId: IDS.josiane } },
  ),
  'messagerie.fils': ok(page([fil]), { requete: { statut: 'rattache' } }),
  'messagerie.fil': ok({ ...fil, messages: [message] }, { parametres: { filId: IDS.fil } }),
  'messagerie.rattacher': cree(fil, { parametres: { filId: IDS.fil }, corps: { dossierId: IDS.dossier, personId: IDS.marc } }),
  'messagerie.envoyer': cree(sortant, {
    corps: {
      channelAccountId: IDS.waBrigitte,
      personId: IDS.marc,
      dossierId: IDS.dossier,
      templateCode: 'R2',
      body: sortant.renderedBody,
      scheduledAt: sortant.scheduledAt,
      attachmentIds: [],
    },
  }),
  'messagerie.sortants.lister': ok(
    [{ ...sortant, channelAccountId: IDS.waIsabelle, state: 'pending_validation', validationRequiredByRole: 'dirigeante' }],
    { requete: { etat: 'pending_validation' } },
  ),
  'messagerie.valider': cree(
    { ...sortant, channelAccountId: IDS.waIsabelle, state: 'scheduled' },
    { parametres: { messageId: IDS.sortant }, corps: { decision: 'valider' } },
  ),
  'messagerie.apercuLien': ok(
    {
      siteName: 'Lakou · Cabinet MAAT',
      title: 'Envoyer une pièce',
      description: 'Déposez une photo de votre document en quelques secondes.',
      image: null,
      landing: 'piece',
    },
    { requete: { page: 'piece' } },
  ),
  'relances.plans': ok([plan], { requete: { dossierId: IDS.dossier } }),
  'relances.pause': cree(
    {
      ...plan,
      state: 'pause',
      pausedReason: 'On laisse respirer (deuil)',
      pausedUntil: '2026-10-28',
      nextActionAt: null,
      nextAction: null,
    },
    { parametres: { planId: IDS.plan }, corps: { reason: 'On laisse respirer (deuil)', until: '2026-10-28' } },
  ),
  'relances.reprise': cree(plan, { parametres: { planId: IDS.plan } }),
  'relances.simuler': ok(
    {
      actions: [
        { at: '2026-10-15T09:00:00-04:00', kind: 'message', channel: 'whatsapp', templateCode: 'R2', blockedBy: null },
        { at: '2026-10-19T10:00:00-04:00', kind: 'appel', channel: null, templateCode: null, blockedBy: null },
        {
          at: '2026-10-22T10:00:00-04:00',
          kind: 'message',
          channel: 'email',
          templateCode: 'E2',
          blockedBy: 'plafond de 2 messages par semaine',
        },
      ],
    },
    { corps: { dossierId: IDS.dossier, personId: IDS.marc, from: INSTANT } },
  ),
  'modeles.lister': ok([modele]),
  'modeles.versionner': cree(
    { ...modele, versionNo: 4, status: 'brouillon' },
    { parametres: { code: 'A1' }, corps: { body: modele.body } },
  ),
  'facturation.dossier': ok(facturation, { parametres: D }),
  'facturation.regles': cree(
    { ...regles, versionNo: 3 },
    { parametres: D, corps: { reason: 'Remise accordée par Isabelle', items: regles.items } },
  ),
  'facturation.collecte': ok(collecte, { parametres: D }),
  'facturation.echeancier': cree(collecte, { parametres: D, corps: { personId: IDS.marc, total: euros(750), count: 2 } }),
  'facturation.factures.emettre': cree(facturation.invoices[0], { parametres: D, corps: { payerPersonId: IDS.josiane } }),
  'paiements.liens': cree(
    [{ id: IDS.lien, url: 'https://paiement.lakou.test/l/7KX2', expiresAt: '2026-10-21T09:00:00-04:00', provider: 'gocardless' }],
    { parametres: D, corps: { installmentIds: [IDS.echeanceMarc1], provider: 'gocardless' } },
  ),
  'confidentialite.consentements': ok([consentement], { parametres: { personId: IDS.marc } }),
  'confidentialite.consentements.enregistrer': cree(consentement, {
    parametres: { personId: IDS.marc },
    corps: { purpose: 'contact_cabinet', granted: true, channel: 'portail' },
  }),
  'confidentialite.sensibles': ok([sensible], { parametres: { personId: IDS.helene } }),
  'confidentialite.destinataires': ok(
    [
      { userId: '0192a6c0-0000-7000-8000-000000000c01', role: 'clerc', displayName: 'Clerc de l’étude fictive' },
      { userId: '0192a6c0-0000-7000-8000-000000000c02', role: 'notaire', displayName: 'Notaire de l’étude fictive' },
    ],
    { parametres: D },
  ),
  'confidentialite.sensibles.partager': cree(
    { ...sensible, shares: [{ recipientDisplayName: 'Cynthia (clerc)', grantedAt: INSTANT, revokedAt: null }] },
    {
      parametres: { etiquetteId: IDS.sensible },
      corps: {
        recipients: [{ userId: IDS.cynthia, role: 'clerc', displayName: 'Cynthia (clerc)' }],
        acknowledgedRecipients: ['Cynthia (clerc)'],
      },
    },
  ),
  'comptesRendus.lister': ok([compteRenduAFaire, compteRendu], { parametres: D }),
  'demarrage.lire': ok(demarrageDossier, { parametres: D }),
  'demarrage.marquer': cree(
    { ...demarrageDossier, etapes: [...demarrageDossier.etapes, { ...etapeDemarrage, codeEtape: 'arbre_sommaire' }] },
    { parametres: { ...D, codeEtape: 'arbre_sommaire' }, corps: { etat: 'fait', versionCatalogue: '2026-10-30.1' } },
  ),
  'demarrage.repondre': cree(
    {
      ...demarrageDossier,
      etapes: [
        ...demarrageDossier.etapes,
        { ...etapeDemarrage, codeEtape: 'dettes_annoncees', etat: null, faitLe: null, faitPar: null, reponse: 'non' },
      ],
    },
    { parametres: { ...D, codeEtape: 'dettes_annoncees' }, corps: { reponse: 'non', versionCatalogue: '2026-10-30.1' } },
  ),
  'demarrage.cocher': cree(
    {
      ...demarrageDossier,
      etapes: [
        ...demarrageDossier.etapes,
        { ...etapeDemarrage, codeEtape: 'devis', etat: null, faitLe: null, faitPar: null, conditionsCochees: ['devis_signe'] },
      ],
    },
    { parametres: { ...D, codeEtape: 'devis' }, corps: { codeCondition: 'devis_signe', coche: true, versionCatalogue: '2026-10-30.5' } },
  ),
  'demarrage.forcer': cree(
    {
      ...demarrageDossier,
      etapes: [...demarrageDossier.etapes, { ...etapeDemarrage, codeEtape: 'arbre_sommaire' }],
      forcages: [
        {
          id: '0192a6c0-0000-7000-8000-0000000000f1',
          portee: 'etape' as const,
          codeEtape: 'arbre_sommaire',
          conditions: ['Mandat signé au dossier'],
          motif: 'Mandat signé sur papier',
          forcePar: 'Isabelle',
          forceLe: '2026-10-30T10:00:00-04:00',
        },
      ],
    },
    {
      parametres: D,
      corps: {
        portee: 'etape',
        codeEtape: 'arbre_sommaire',
        versionCatalogue: '2026-10-30.4',
        conditions: ['Mandat signé au dossier'],
        motif: 'Mandat signé sur papier',
      },
    },
  ),
  'demarrage.demanderDeblocage': cree(
    { tacheId: '0192a6c0-0000-7000-8000-0000000000a9', prevenue: 'Isabelle' },
    { parametres: D, corps: { portee: 'mandat_recherche', conditions: ['Mandat signé au dossier'] } },
  ),
  'accueil.vocaux.lister': ok([vocalExemple], { parametres: D }),
  'accueil.vocaux.ouvrir': cree(
    { ...vocalExemple, recus: 0, statut: 'televersement' as const },
    {
      parametres: D,
      corps: { nomFichier: 'entretien.m4a', mime: 'audio/mp4', taille: 4 },
    },
  ),
  'accueil.vocaux.morceau': cree(
    { ...vocalExemple, statut: 'televersement' as const },
    {
      parametres: { ...D, vocalId: vocalExemple.id },
      corps: { index: 0, donnees: 'AAECAw==' },
    },
  ),
  'accueil.vocaux.terminer': cree(vocalExemple, { parametres: { ...D, vocalId: vocalExemple.id }, corps: {} }),
  'demarrage.mode.lire': ok({ mode: 'avance' as const }),
  'demarrage.mode.choisir': cree({ mode: 'guide' as const }, { corps: { mode: 'guide' } }),
  'demarrage.tableau': ok({
    dossiers: [{ dossierId: IDS.dossier, reference: 'MAAT-2026-101', titre: 'Succession Bélénus', etapes: [etapeDemarrage] }],
  }),
  'temps.dossier': ok(tempsDossier, { parametres: D }),
  'temps.enregistrer': cree(tempsDossier, {
    parametres: D,
    corps: { startedAt: '2026-10-27T09:00:00-04:00', endedAt: '2026-10-27T09:10:00-04:00' },
  }),
  'temps.corriger': cree(
    { ...tempsDossier, totalMs: 2_700_000, entries: [...tempsDossier.entries, correctionTemps] },
    { parametres: D, corps: { totalMs: 2_700_000, reason: 'Appel de 35 min à Marc non chronométré' } },
  ),
  'comptesRendus.creer': cree(compteRenduAFaire, { parametres: D, corps: { kind: 'entretien', title: 'Entretien avec Nadia C.' } }),
  'comptesRendus.generer': ok(compteRendu, {
    parametres: { compteRenduId: IDS.compteRendu },
    corps: { sourceText: 'Marc confirme l’adresse de sa sœur Sandra au Moule.' },
  }),
  'comptesRendus.modifier': ok(
    { ...compteRendu, rowVersion: 3 },
    { parametres: { compteRenduId: IDS.compteRendu }, corps: { body: compteRendu.body } },
  ),
  'comptesRendus.valider': ok(
    { ...compteRendu, status: 'valide', validatedAt: '2026-10-15T10:00:00-04:00', validatedByDisplayName: 'Isabelle', rowVersion: 3 },
    { parametres: { compteRenduId: IDS.compteRendu }, corps: { rowVersion: 2 } },
  ),
  'comptesRendus.aTraiter': ok({
    aFaire: [{ ...compteRenduAFaire, ...dansDossier }],
    aRelire: [{ ...compteRendu, ...dansDossier }],
    valides: [],
  }),
  'actesNotaries.lister': ok(
    {
      types: [
        { code: 'notoriete', label: 'Acte de notoriété' },
        { code: 'attestation_immobiliere', label: 'Attestation immobilière' },
        { code: 'partage', label: 'Acte de partage' },
      ],
      successions: [{ id: IDS.succAlbert, deceasedDisplayName: 'Albert Bélénus' }],
      actes: [acteNotarie],
    },
    { parametres: D },
  ),
  'actesNotaries.creer': cree(acteNotarie, {
    parametres: D,
    corps: { kind: 'notoriete', status: 'signe', successionId: IDS.succAlbert, actDate: '2012-05-03', documentId: IDS.documentActe },
  }),
  'actesNotaries.modifier': ok(
    { ...acteNotarie, status: 'publie', rowVersion: 2 },
    { parametres: { acteId: IDS.acteNotarie }, corps: { status: 'publie' } },
  ),
  'actesNotaries.retirer': ok(undefined, { parametres: { acteId: IDS.acteNotarie } }, 204),
  'genealogie.contacts.apercu': ok(apercuImport, { parametres: D, corps: { csv: CSV_IMPORT, fileName: 'contacts.csv' } }),
  'genealogie.contacts.importer': cree(
    { createdCount: 1, duplicateCount: 1, errorCount: 1, persons: [{ id: IDS.contactImporte, displayName: 'Nadia Fictive' }] },
    { parametres: D, corps: { csv: CSV_IMPORT, fileName: 'contacts.csv', fingerprint: apercuImport.fingerprint, expectedCount: 1 } },
  ),
  'videos.lister': ok({ videos: [videoDossier], recipients: [{ ...destinataireVideo, role: 'heritier' }] }, { parametres: D }),
  'videos.rattacher': cree(videoDossier, { parametres: D, corps: { title: videoDossier.title, url: videoDossier.url } }),
  'videos.retirer': ok(undefined, { parametres: { videoId: IDS.video } }, 204),
  'videos.partager': ok(
    {
      ...videoDossier,
      sharedWithFamilyAt: '2026-10-15T10:00:00-04:00',
      sharedByDisplayName: 'Isabelle',
      sharedWith: ['Marc Bélénus'],
      rowVersion: 2,
    },
    { parametres: { videoId: IDS.video }, corps: { recipients: [destinataireVideo], acknowledgedRecipients: ['Marc Bélénus'] } },
  ),
  'videos.partage.retirer': ok({ ...videoDossier, rowVersion: 3 }, { parametres: { videoId: IDS.video } }),
  'portail.videos': ok([{ id: IDS.video, title: videoDossier.title, embedUrl: videoDossier.embedUrl }]),
  'facturation.supplements.lister': ok(
    {
      catalogue: [
        { code: 'recherche_titre', label: 'Recherche de titre', unitAmountCents: null },
        { code: 'deplacement', label: 'Déplacement physique', unitAmountCents: null },
      ],
      catalogueStatus: 'a_valider',
      supplements: [supplement],
      billableTotalCents: 15_000,
      notBilledTotalCents: 0,
      fraisReelsActive: true,
    },
    { parametres: D },
  ),
  'facturation.supplements.ajouter': cree(supplement, { parametres: D, corps: { code: 'recherche_titre', unitAmountCents: 15_000 } }),
  'facturation.supplements.retirer': ok(undefined, { parametres: { supplementId: IDS.supplement } }, 204),
  'facturation.prestations.lister': ok(
    {
      version: '1.0.0',
      status: 'a_valider',
      prestations: [{ code: designationRegsuc.code as string, intitule: designationRegsuc.intitule, detail: designationRegsuc.detail }],
    },
    { parametres: D },
  ),
  'facturation.devis.lister': ok([devisDetaille], { parametres: D }),
  'facturation.devis.creer': cree(devisDetaille, {
    parametres: D,
    corps: { phaseScope: 'jusqu_indivision', lignes: [{ prestationCode: 'REGSUC-SUPERDEFTRANS', amountCents: 250_000 }] },
  }),
  'facturation.devis.pdf': ok(exportPdf, { parametres: { dossierId: IDS.dossier, devisId: IDS.devisDetaille } }),
  'facturation.factures.pdf': ok(exportPdf, { parametres: { dossierId: IDS.dossier, factureId: IDS.facture } }),
  'compensations.dossier': ok(
    {
      dossierId: IDS.dossier,
      warning:
        'La renonciation n’est pas forcément la meilleure solution : elle engage aussi les enfants de l’héritier et bloque la succession.',
      options: [
        {
          code: 'foncier',
          label: 'Règlement en nature (foncier)',
          description: 'Cession d’une partie de sa part en terrain.',
          recommended: true,
        },
        {
          code: 'echelonnement',
          label: 'Paiement échelonné',
          description: 'Jusqu’à six échéances avant la signature.',
          recommended: false,
        },
        { code: 'renonciation', label: 'Renonciation à la succession', description: 'Dernier recours.', recommended: false },
      ],
      debts: [
        {
          creditorPersonId: IDS.marc,
          creditorDisplayName: 'Marc Bélénus',
          debtorPersonId: IDS.sandra,
          debtorDisplayName: 'Sandra Bélénus',
          amount: { amountCents: 75_000, currency: 'EUR' },
        },
      ],
      calculation: {
        status: 'a_valider',
        note: 'Calcul à valider : la valeur retenue pour un foncier n’est pas encore décidée.',
        mention: 'Estimation indicative, ne vaut pas évaluation',
      },
      unavailableReason: null,
      dataVersion: '1.0.0',
    },
    { parametres: D },
  ),
  'confidentialite.notes': ok([note], { parametres: D }),
  'confidentialite.notes.ajouter': cree(note, { parametres: D, corps: { personId: IDS.filleLucien, body: note.body, isHypothesis: true } }),
  'confidentialite.demandes': cree({ id: IDS.rgpd, kind: 'acces', status: 'recue', receivedAt: INSTANT }, { corps: { kind: 'acces' } }),
  'admin.utilisateurs': ok(page([isabelle, brigitte, cynthia])),
  'admin.utilisateurs.creer': cree(cynthia, {
    corps: {
      email: cynthia.email,
      displayName: 'Cynthia',
      kind: 'external',
      roles: [{ role: 'clerc', scopeType: 'etude', scopeId: IDS.etude }],
    },
  }),
  'admin.etudes': ok([etude]),
  'admin.etudes.creer': cree(etude, {
    corps: { name: 'Étude principale', address: 'Pointe-à-Pitre', notaireTitulaire: 'Notaire titulaire (fictif)' },
  }),
  'admin.etudes.modifier': ok(
    { ...etude, rowVersion: 3 },
    { parametres: { etudeId: IDS.etude }, corps: { address: 'Pointe-à-Pitre, Guadeloupe' } },
  ),
  'admin.etudes.supprimer': ok(undefined, { parametres: { etudeId: IDS.etude } }, 204),
  'admin.parametresLegaux': ok([parametre]),
  'admin.parametresLegaux.versionner': cree(parametre, {
    corps: {
      key: parametre.key,
      value: 10,
      effectiveFrom: '2024-04-10',
      effectiveTo: '2038-12-31',
      sourceUrl: parametre.sourceUrl,
      note: parametre.note,
    },
  }),
  'beta.questionsDues': ok([questionBeta], { requete: { contexte: 'D04c.afficher_au_client' } }),
  'beta.repondre': cree(
    { ...reglage, key: 'estimation.visible_client', value: 'jamais' },
    { parametres: { cle: 'estimation.visible_client' }, corps: { option: 'jamais' } },
  ),
  'beta.reglages': ok([reglage]),
  'portail.accueil': ok(accueil),
  'portail.branche': ok(brancheMarc),
  'portail.paiement': ok(facturationClient),
  'portail.prestations': ok({ devis: [{ id: IDS.devisDetaille, status: 'envoye', lines: [designationRegsuc] }], factures: [] }),
  'portail.journal': ok([
    { at: '2026-10-02T18:40:00-04:00', kind: 'consentement', summary: 'Vous avez accepté d’être contacté par le cabinet.' },
    { at: '2026-10-11T10:12:00-04:00', kind: 'piece', summary: 'Vous avez envoyé une photo de votre acte de naissance.' },
  ]),
  'portail.confidentialite': ok(confidentialite),
  'portail.confidentialite.modifier': cree(confidentialite, { corps: { preferredChannel: 'email' } }),
  'portail.pieces.deposer': cree(
    { uploadUrl: '/api/v1/televersements/portail-7KX2', expiresAt: '2026-10-14T21:00:00-04:00', maxSizeBytes: 15_000_000 },
    { corps: { requirementId: IDS.exigence, mime: 'image/jpeg', size: 812345 } },
  ),
  'portail.pieces.demander': ok({ requirementId: IDS.exigence, delegatedRequest: true }, { corps: { requirementId: IDS.exigence } }),
  'clerc.dossiers': ok([clercDossier]),
  'notaire.dossier': ok(
    {
      id: IDS.dossier,
      reference: 'BEL-2026-001',
      title: 'Succession Bélénus',
      living: vivants,
      deceased: defunts,
      acts: [
        { label: 'Acte de notoriété d’Albert B.', status: 'en_cours', dueAt: null },
        { label: 'Attestation immobilière', status: 'a_preparer', dueAt: '2026-11-30' },
      ],
      titles: [{ assetLabel: 'Terrain de Grand-Bourg', titleStatus: 'trouve' }],
    } satisfies T<typeof E.NotaireDossierSchema>,
    { parametres: D },
  ),
  'sync.push': ok(
    { results: [{ mutationId: uuid(0xc01), result: 'applique', conflict: null }] },
    {
      corps: {
        deviceId: 'iphone-brigitte',
        mutations: [{ mutationId: uuid(0xc01), entityType: 'call', entityId: uuid(0xc02), baseVersion: 0, op: { outcome: 'joint' } }],
      },
    },
  ),
  'sync.pull': ok(
    { changes: [{ seq: '1042', entityType: 'task', entityId: IDS.tache, op: 'upsert', rowVersion: 2 }], cursor: '1042' },
    { requete: { depuis: '1000' } },
  ),
  'webhooks.jockalia': webhook({ event: 'message.received', id: 'evt-fictif-1' }),
  'webhooks.email': webhook({ messageId: '<fictif-1@exemple.test>' }),
  'webhooks.stripe': webhook({ id: 'evt_fictif_1', type: 'checkout.session.completed' }),
  'webhooks.gocardless': webhook({ events: [{ id: 'EV-fictif-1', action: 'confirmed' }] }),
  'webhooks.docuseal': webhook({ event_type: 'submission.completed', submission_id: 'fictif-1' }),
  'systeme.sante': ok({ status: 'ok', service: 'lakou-api' }),
  'systeme.version': ok({
    service: 'lakou-api',
    commit: '0000000000000000000000000000000000000000',
    commitCourt: '0000000',
    branch: 'mock',
    buildDate: '2026-10-14T09:00:00-04:00',
    environment: 'mock',
    container: 'mock',
  }),
};
